import { Check, Star } from "lucide-react";
import { useLang } from "@/landing/LanguageContext";
import { Reveal, Overline, CTAButton } from "@/landing/ui";

const PricingSection = () => {
  const { t } = useLang();
  const p = t.pricing;

  return (
    <section id="inversion" className="scroll-mt-20 bg-beige py-24 text-carbon lg:py-32" data-testid="pricing-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <Reveal className="mb-12 max-w-3xl">
          <Overline>{p.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:text-4xl lg:text-5xl">
            {p.title}
          </h2>
          <div className="mt-7 space-y-4">
            {p.intro.map((para, i) => (
              <p key={i} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                {para}
              </p>
            ))}
          </div>
        </Reveal>

        {/* Pricing plans */}
        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-3">
          {p.plans.map((plan, i) => (
            <Reveal key={i} delay={i * 0.08} className={`${["order-3 lg:order-1", "order-1 lg:order-2", "order-2 lg:order-3"][i] || ""} ${plan.featured ? "lg:-my-4" : ""}`}>
              <div
                data-testid={`pricing-plan-${i}`}
                className={`relative flex h-full flex-col p-8 lg:p-10 ${
                  plan.featured
                    ? "bg-carbon text-cream shadow-2xl ring-1 ring-sand/40"
                    : "border border-earth/25 bg-cream text-carbon"
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-sand px-4 py-1 font-sans text-xs font-semibold tracking-[0.16em] uppercase text-carbon">
                    <Star size={12} className="fill-carbon" /> {plan.badge}
                  </span>
                )}
                {plan.seal && (
                  <span className="absolute -top-6 -right-4 flex h-20 w-20 rotate-12 items-center justify-center rounded-full border border-sand/70 bg-carbon p-2 text-center font-sans text-[10px] font-semibold uppercase leading-tight tracking-[0.1em] text-sand shadow-lg">
                    {plan.seal}
                  </span>
                )}
                <p
                  className={`font-sans text-xs tracking-[0.2em] uppercase ${
                    plan.featured ? "text-sand" : "text-earth/70"
                  }`}
                >
                  {plan.name}
                </p>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-serif text-4xl font-light lg:text-5xl">{plan.price}</span>
                  {plan.priceNote && (
                    <span
                      className={`font-sans text-xs tracking-wide ${
                        plan.featured ? "text-cream/60" : "text-earth/70"
                      }`}
                    >
                      {plan.priceNote}
                    </span>
                  )}
                </div>
                <p
                  className={`mt-5 font-sans text-sm font-light leading-relaxed ${
                    plan.featured ? "text-cream/80" : "text-earth"
                  }`}
                >
                  {plan.desc}
                </p>

                {plan.pointsLabel && (
                  <p
                    className={`mt-6 font-sans text-xs tracking-[0.2em] uppercase ${
                      plan.featured ? "text-sand" : "text-earth/70"
                    }`}
                  >
                    {plan.pointsLabel}
                  </p>
                )}
                {plan.points && (
                  <ul className={`${plan.pointsLabel ? "mt-3" : "mt-6"} space-y-3`}>
                    {plan.points.map((pt, k) => (
                      <li key={k} className="flex items-start gap-3">
                        <Check
                          size={16}
                          className={`mt-1 shrink-0 ${plan.featured ? "text-sand" : "text-earth"}`}
                        />
                        <span
                          className={`font-sans text-sm font-light leading-relaxed ${
                            plan.featured ? "text-cream/85" : "text-earth"
                          }`}
                        >
                          {pt}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-auto pt-8">
                  {plan.cta && (
                    <CTAButton
                      testid={`pricing-cta-${i}`}
                      source={`pricing-${plan.featured ? "preferente" : "plan-" + i}`}
                      variant={plan.featured ? "light" : "outline"}
                      className="w-full"
                    >
                      {plan.cta}
                    </CTAButton>
                  )}
                  {plan.ctaNote && (
                    <p className="mt-4 font-sans text-xs leading-relaxed text-cream/55">{plan.ctaNote}</p>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Why 72h */}
        <Reveal delay={0.1} className="mx-auto mt-20 max-w-3xl">
          <h3 className="font-serif text-2xl font-light leading-snug text-carbon lg:text-3xl">
            {p.whyTitle}
          </h3>
          <div className="mt-6 space-y-4">
            {p.why.map((para, i) => (
              <p key={i} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                {para}
              </p>
            ))}
          </div>

          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {p.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check size={18} className="mt-1 shrink-0 text-earth" />
                <span className="font-sans text-sm font-light leading-relaxed text-carbon lg:text-base">
                  {b}
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-10 font-serif text-xl font-light italic leading-snug text-carbon lg:text-2xl">
            {p.closing}
          </p>

          <div className="mt-8">
            <CTAButton testid="pricing-closing-cta" source="pricing-closing" variant="solid">
              {p.closingCta}
            </CTAButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PricingSection;
